import React from 'react';
import { Box, Button, Container, IconButton, InputAdornment, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useAuth } from '@wheeebo/polaris-api';
import { useColorMode } from '../colorMode';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
const msgs = defineMessages({
    buttonLogin: {
        id: 'dialog.LoginDialog.button.login',
        defaultMessage: 'Log in',
    },
    buttonForgotPassword: {
        id: 'dialog.LoginDialog.button.forgotPassword',
        defaultMessage: 'Forgot password',
    },
    buttonSignUp: {
        id: 'dialog.LoginDialog.button.signUp',
        defaultMessage: 'Create a new account',
    },
    headertitle: {
        id: 'dialog.LoginDialog.header.title',
        defaultMessage: 'DX-Web Platform',
    },
    errorNoSignInModule: {
        id: 'dialog.LoginDialog.error.noSignInModule',
        defaultMessage: 'the signin module not found',
    },
    errorInvalidLogin: {
        id: 'dialog.LoginDialog.error.invalidLogin',
        defaultMessage: 'invalid login',
    },
    errorLoginFailed: {
        id: 'dialog.LoginDialog.error.loginFailed',
        defaultMessage: 'Login Failed.',
    },
    inputEmail: {
        id: 'dialog.LoginDialog.input.email',
        defaultMessage: 'Email',
    },
    inputPassword: {
        id: 'dialog.LoginDialog.input.password',
        defaultMessage: 'Password',
    },
});
/** Loads the Google Identity Services script once (shared with SignUpPage). */
function useGisScriptLoaded() {
    const [loaded, setLoaded] = React.useState(() => !!window.google?.accounts);
    React.useEffect(() => {
        if (loaded)
            return;
        const existing = document.querySelector('script[src*="gsi/client"]');
        if (existing) {
            setLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => setLoaded(true);
        document.head.appendChild(script);
    }, [loaded]);
    return loaded;
}
function LoginPage({ disableSignup, onGoogleSignIn, googleClientId }) {
    const intl = useIntl();
    const colorMode = useColorMode();
    const theme = useTheme();
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { register, formState, handleSubmit: validate } = useForm();
    const { errors } = formState;
    const [email, setEmail] = React.useState();
    const [password, setPassword] = React.useState();
    const [passwordShown, setShowPasswordShown] = React.useState(false);
    const googleBtnRef = React.useRef(null);
    const gisLoaded = useGisScriptLoaded();
    const { mutate, isLoading } = useMutation(() => {
        if (!signIn || !email || !password) {
            return Promise.reject(intl.formatMessage(msgs.errorInvalidLogin));
        }
        return signIn(email, password);
    }, {
        onSuccess: () => {
            if (location?.state?.from) {
                navigate(`${location?.state?.from?.pathname}${location?.state?.from?.search || ''}`);
            }
        },
        onError: showAndEmitJsErrorEvent,
    });
    const { mutate: googleSignIn } = useMutation((idToken) => Promise.resolve(onGoogleSignIn?.(idToken)), {
        onSuccess: () => {
            if (location?.state?.from) {
                navigate(`${location?.state?.from?.pathname}${location?.state?.from?.search || ''}`);
            }
        },
        onError: showAndEmitJsErrorEvent,
    });
    React.useEffect(() => {
        const googleAccounts = window.google?.accounts;
        if (!gisLoaded || !onGoogleSignIn || !googleClientId || !googleAccounts || !googleBtnRef.current)
            return;
        googleAccounts.id.initialize({
            client_id: googleClientId,
            callback: (response) => {
                if (response.credential) {
                    googleSignIn(response.credential);
                }
            },
        });
        googleAccounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            width: '100%',
        });
    }, [gisLoaded, onGoogleSignIn, googleClientId, googleSignIn]);
    const handleSubmit = () => mutate();
    return (React.createElement(Container, { component: "main", sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, my: 8, p: 3 }, variant: "outlined" },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Box, { sx: { textAlign: 'center' } },
                        React.createElement(Typography, { variant: "h5", color: colorMode.isThemeDark ? undefined : 'primary', sx: { letterSpacing: '0.15rem' } }, "Polaris"),
                        React.createElement(Typography, { variant: "caption", sx: { textAlign: 'center' }, color: colorMode.isThemeDark ? undefined : 'primary' }, "Wheeebo Central Command Center")),
                    onGoogleSignIn && googleClientId && (React.createElement(React.Fragment, null,
                        React.createElement(Box, { ref: googleBtnRef, sx: { width: '100%', minHeight: 44 } }),
                        React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
                            React.createElement(Box, { sx: { flex: 1, height: 1, bgcolor: 'divider' } }),
                            React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u307E\u305F\u306F"),
                            React.createElement(Box, { sx: { flex: 1, height: 1, bgcolor: 'divider' } })))),
                    React.createElement(ValidatableField, { autoFocus: true, id: "email", type: "email", autoComplete: "email", label: "Email", fullWidth: true, size: "medium", disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            maxLength: 128,
                            onChange: (e) => setEmail(e.target.value),
                        }, inputProps: {
                            sx: {
                                '&:-webkit-autofill': {
                                    WebkitBoxShadow: `0 0 0 30px ${theme.palette.background.paper} inset`,
                                },
                            },
                        } }),
                    React.createElement(ValidatableField, { id: "password", label: "Password", fullWidth: true, autoComplete: "current-password", type: passwordShown ? 'text' : 'password', disabled: isLoading, errors, register, registerOptions: {
                            required: true,
                            maxLength: 128,
                            onChange: (e) => setPassword(e.target.value),
                        }, InputProps: {
                            endAdornment: (React.createElement(InputAdornment, { position: "start", sx: { mx: 1 } },
                                React.createElement(IconButton, { onClick: () => setShowPasswordShown((e) => !e), edge: "end" }, passwordShown ? React.createElement(VisibilityOffIcon, null) : React.createElement(VisibilityIcon, null)))),
                        }, inputProps: {
                            sx: {
                                '&:-webkit-autofill': {
                                    WebkitBoxShadow: `0 0 0 30px ${theme.palette.background.paper} inset`,
                                },
                            },
                        } }),
                    React.createElement(Button, { disabled: isLoading, type: "submit", size: "medium", sx: { mt: 1 }, color: "primary" }, "Log in"),
                    React.createElement(Stack, { direction: "row", sx: { width: '100%', justifyContent: disableSignup ? 'center' : 'space-between' } },
                        React.createElement(Button, { color: colorMode.isThemeDark ? 'inherit' : 'primary', variant: "text", sx: { textTransform: 'none', textDecoration: 'none' }, onClick: () => navigate('/forgot_password') }, intl.formatMessage(msgs.buttonForgotPassword)),
                        disableSignup ? null : (React.createElement(Button, { color: colorMode.isThemeDark ? 'inherit' : 'primary', variant: "text", sx: { textTransform: 'none', textDecoration: 'none' }, onClick: () => navigate('/signup') }, intl.formatMessage(msgs.buttonSignUp)))))))));
}
export default React.memo(LoginPage);
